// src/api.js
// All API calls now use credentials: 'include' to send httpOnly cookies
// This is more secure than localStorage tokens (prevents XSS theft)

const BASE = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");

// ============================================================================
// CSRF TOKEN (preparation phase — enforcement not yet active)
// ============================================================================

let _csrfToken = null;

// Fetch a CSRF token from the backend and store it for use in mutating requests.
// Non-fatal: if this fails, requests still proceed (enforcement is off).
export async function fetchCsrfToken() {
  try {
    const res = await fetch(`${BASE}/csrf-token`, { credentials: 'include' });
    if (res.ok) {
      const data = await res.json();
      _csrfToken = data.csrfToken || res.headers.get('X-CSRF-Token') || null;
    }
  } catch {
    // Non-fatal — detection mode only, app works without token
  }
}

// Methods that require a CSRF token (backend excludes GET/HEAD/OPTIONS)
const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

// Returns CSRF header object with method check + race-condition handling.
// Use at direct fetch call sites instead of fetchOptions().
// If token not yet ready on a mutating request: triggers background fetch,
// allows request to proceed without header (backend is detection-only).
export function csrfHeadersFor(method) {
  if (!MUTATING_METHODS.has((method || 'GET').toUpperCase())) return {};
  if (_csrfToken) return { 'X-CSRF-Token': _csrfToken };
  fetchCsrfToken(); // race: fetch in background, request proceeds without it
  return {};
}

// Standard headers for API calls
function authHeaders(extra = {}) {
  return { ...extra };
}

// Standard fetch options with credentials
function fetchOptions(options = {}) {
  const method = (options.method || 'GET').toUpperCase();
  const isMutating = MUTATING_METHODS.has(method);

  // Attach CSRF token only on state-mutating methods
  let csrfHeader = {};
  if (isMutating) {
    if (_csrfToken) {
      csrfHeader = { 'X-CSRF-Token': _csrfToken };
      console.log('[CSRF] token attached'); // TODO: remove after rollout verified
    } else {
      // Token not yet available — fetch in background, request proceeds without it.
      // Backend is in detection-only mode so this is safe during preparation phase.
      fetchCsrfToken();
    }
  }

  return {
    ...options,
    credentials: 'include', // Send httpOnly cookies
    headers: {
      ...authHeaders(options.headers || {}),
      ...csrfHeader
    }
  };
}

// Health
export async function apiHealth() {
  const res = await fetch(`${BASE}/health`, fetchOptions());
  if (!res.ok) throw new Error(`Health failed: ${res.status}`);
  return await res.json();
}

// Clients
export async function getClients(limit = 1000) {
  const res = await fetch(`${BASE}/clients?limit=${limit}`, fetchOptions());
  if (!res.ok) throw new Error(`GET /clients failed: ${res.status}`);
  return (await res.json()).data || [];
}
export async function getClientById(id) {
  const res = await fetch(`${BASE}/clients/${id}`, fetchOptions());
  if (!res.ok) throw new Error(`GET /clients/${id} failed: ${res.status}`);
  return (await res.json()).data;
}
export async function addClient(payload) {
  const res = await fetch(`${BASE}/clients`, fetchOptions({
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  }));
  const data = await res.json().catch(() => ({}));
  console.log('[api.addClient] Response status:', res.status, 'data:', data);
  if (!res.ok) {
    // For duplicate detection, return the response with duplicate flag so UI can show merge modal
    if (res.status === 409 && data.duplicate) {
      console.log('[api.addClient] Duplicate detected, returning duplicate data');
      return { isDuplicate: true, duplicate: data.duplicate, error: data.error };
    }
    throw new Error(data.error || `POST /clients failed: ${res.status}`);
  }
  return data;
}
export async function updateClient(id, patch) {
  const res = await fetch(`${BASE}/clients/${id}`, fetchOptions({
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  }));
  if (!res.ok) { const msg = await res.text().catch(()=>res.statusText); throw new Error(msg || `PUT /clients/${id} failed`); }
  return (await res.json()).data;
}
export async function deleteClient(id) {
  const res = await fetch(`${BASE}/clients/${id}`, fetchOptions({ method: "DELETE" }));

  // Handle 204 No Content (success with no body)
  if (res.status === 204) {
    return { ok: true };
  }

  // Parse response body once
  const data = await res.json().catch(() => ({}));

  // Check for HTTP errors
  if (!res.ok) {
    throw new Error(data.error || `DELETE /clients/${id} failed: ${res.status}`);
  }

  // Check if the response indicates failure even with 200 status
  if (!data.ok) {
    throw new Error(data.error || 'Delete failed');
  }

  return data;
}

// Import/Export
export async function bulkImportClients(items, upsert = true, filename = 'import.csv', resolvedDuplicates = null) {
  const res = await fetch(`${BASE}/clients/bulk`, fetchOptions({
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ items, upsert, filename, resolvedDuplicates }),
  }));
  if (!res.ok) {
    const errText = await res.text().catch(() => '');
    throw new Error(errText || `bulk import failed: ${res.status}`);
  }
  return await res.json();
}

// Check for potential duplicates before import
export async function checkDuplicates(items) {
  const res = await fetch(`${BASE}/clients/check-duplicates`, fetchOptions({
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ items }),
  }));
  if (!res.ok) {
    const errText = await res.text().catch(() => '');
    throw new Error(errText || `duplicate check failed: ${res.status}`);
  }
  return await res.json();
}
export function getExportCsvUrl() {
  // For file downloads, we use a different approach - open in new window with cookie auth
  return `${BASE}/clients/export.csv`;
}
export async function getImportHistory() {
  const res = await fetch(`${BASE}/clients/imports`, fetchOptions());
  if (!res.ok) throw new Error(`GET /clients/imports failed: ${res.status}`);
  return (await res.json()).data || [];
}
export async function reverseImport(importId) {
  const res = await fetch(`${BASE}/clients/imports/${importId}/reverse`, fetchOptions({
    method: "POST",
  }));
  if (!res.ok) {
    const errText = await res.text().catch(() => '');
    throw new Error(errText || `reverse import failed: ${res.status}`);
  }
  return await res.json();
}

// Uploads
export async function listClientUploads(id) {
  const res = await fetch(`${BASE}/clients/${id}/uploads`, fetchOptions());
  if (!res.ok) throw new Error(`GET /clients/${id}/uploads failed: ${res.status}`);
  return (await res.json()).data || [];
}
export async function uploadClientFile(id, file, label = "Document") {
  const fd = new FormData(); fd.append("file", file); fd.append("label", label);
  const res = await fetch(`${BASE}/clients/${id}/uploads`, fetchOptions({ method: "POST", body: fd }));
  if (!res.ok) throw new Error(`POST /clients/${id}/uploads failed: ${res.status}`);
  return (await res.json()).data;
}
export async function deleteClientUpload(id, uploadId) {
  const res = await fetch(`${BASE}/clients/${id}/uploads/${uploadId}`, fetchOptions({ method: "DELETE" }));
  if (!res.ok && res.status !== 204) throw new Error(`DELETE /clients/${id}/uploads/${uploadId} failed: ${res.status}`);
}
export function getUploadDownloadUrl(filename) {
  // For file downloads, we use cookie auth
  return `${BASE}/uploads/${encodeURIComponent(filename)}`;
}

// Enrollment PDF parsing
export async function parseEnrollmentPdf(file) {
  const fd = new FormData();
  fd.append("file", file);
  const res = await fetch(`${BASE}/enrollments/parse`, fetchOptions({
    method: "POST",
    body: fd
  }));
  if (!res.ok) {
    const errText = await res.text().catch(() => res.statusText);
    throw new Error(errText || `PDF parsing failed: ${res.status}`);
  }
  return await res.json();
}

// ---------- Communications (automatic) ----------
export async function listComms(id, limit = 5) {
  const res = await fetch(`${BASE}/clients/${id}/comms?limit=${limit}`, fetchOptions());
  if (!res.ok) throw new Error(`GET /clients/${id}/comms failed: ${res.status}`);
  return (await res.json()).data || [];
}
export async function listCommsAll(id) {
  const res = await fetch(`${BASE}/clients/${id}/comms/all`, fetchOptions());
  if (!res.ok) throw new Error(`GET /clients/${id}/comms/all failed: ${res.status}`);
  return (await res.json()).data || [];
}
export async function addComm(clientId, payload) {
  // Backend expects: POST /comms with { clientId, type, subject, body }
  const body = {
    clientId: clientId,
    type: payload.type,
    subject: payload.subject || '',
    body: payload.body || payload.message || payload.preview || '',
    direction: payload.direction === 'out' ? 'outbound' : (payload.direction === 'in' ? 'inbound' : 'outbound'),
    metadata: payload.meta || {}
  };

  const res = await fetch(`${BASE}/comms`, {
    method: "POST",
    ...fetchOptions({ headers: { "Content-Type": "application/json" } }),
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`POST /comms failed: ${res.status}`);
  return (await res.json()).data;
}

// Keep older import name
export { getClients as fetchClients };

// Update assistant name
export async function updateAssistantName(assistantName) {
  const res = await fetch(`${BASE}/auth/update-assistant-name`, {
    method: "PATCH",
    ...fetchOptions({ headers: { "Content-Type": "application/json" } }),
    body: JSON.stringify({ assistantName })
  });
  if (!res.ok) {
    const msg = await res.text().catch(() => res.statusText);
    throw new Error(msg || "Failed to update assistant name");
  }
  return await res.json();
}

// Get dynamic briefing (time-aware)
export async function getDynamicBriefing() {
  const res = await fetch(`${BASE}/churn-prediction/dynamic-briefing`, {
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`GET /churn-prediction/dynamic-briefing failed: ${res.status}`);
  return await res.json();
}

// Auth + Forgot Password
export async function login(email, password, mfaCode = null) {
  const body = { email, password };
  if (mfaCode) body.mfaCode = mfaCode;

  const res = await fetch(`${BASE}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: 'include', // Receive and store httpOnly cookie
    body: JSON.stringify(body),
  });

  const data = await res.json().catch(() => ({}));

  // Handle MFA required response (this is not an error)
  if (res.ok && data.mfaRequired) {
    return { mfaRequired: true, tempToken: data.tempToken, email };
  }

  if (!res.ok) {
    const err = new Error(data.error || "login failed");
    err.code = res.status;
    // Include lockout info for 423 (Locked) responses
    if (res.status === 423) {
      err.locked = true;
      err.lockedUntil = data.lockedUntil;
      err.remainingMinutes = data.remainingMinutes;
      err.lockoutMinutes = data.lockoutMinutes;
    }
    // Include remaining attempts for failed auth
    if (data.attemptsRemaining !== undefined) {
      err.attemptsRemaining = data.attemptsRemaining;
    }
    throw err;
  }

  return data;
}

// Verify MFA code during login
export async function verifyMfaLogin(tempToken, code) {
  const res = await fetch(`${BASE}/mfa/verify`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${tempToken}` // Temp token for MFA step
    },
    credentials: 'include',
    body: JSON.stringify({ code }),
  });

  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    const err = new Error(data.error || "MFA verification failed");
    err.code = res.status;
    err.attemptsRemaining = data.attemptsRemaining;
    throw err;
  }

  return data;
}

// Verify backup code during login
export async function verifyBackupCodeLogin(tempToken, backupCode) {
  const res = await fetch(`${BASE}/mfa/verify-backup`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${tempToken}` // Temp token for MFA step
    },
    credentials: 'include',
    body: JSON.stringify({ backupCode }),
  });

  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    const err = new Error(data.error || "Backup code verification failed");
    err.code = res.status;
    throw err;
  }

  return data;
}
export async function signup(email, password) {
  const res = await fetch(`${BASE}/auth/signup`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: 'include', // Receive auth cookie
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) {
    const msg = await res.text().catch(()=>res.statusText);
    const err = new Error(msg || "signup failed");
    err.code = res.status;
    throw err;
  }
  return await res.json();
}

export async function requestPasswordReset(email) {
  const res = await fetch(`${BASE}/auth/request-reset`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) throw new Error(`request-reset failed: ${res.status}`);
  return await res.json();
}

export async function resetPassword(email, code, newPassword) {
  const res = await fetch(`${BASE}/auth/reset`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: 'include', // Receive auth cookie on successful reset
    body: JSON.stringify({ email, code, newPassword }),
  });
  if (!res.ok) {
    const msg = await res.text().catch(()=>res.statusText);
    const err = new Error(msg || "reset failed");
    err.code = res.status;
    throw err;
  }
  return await res.json();
}

// ============================================================================
// BLUE BUTTON 2.0 (Medicare Data)
// ============================================================================

export async function getBlueButtonAuthUrl(clientId) {
  const res = await fetch(`${BASE}/bluebutton/connect/${clientId}`, fetchOptions());
  if (!res.ok) throw new Error(`GET /bluebutton/connect failed: ${res.status}`);
  return await res.json();
}

export async function getBlueButtonStatus(clientId) {
  const res = await fetch(`${BASE}/bluebutton/status/${clientId}`, fetchOptions());
  if (!res.ok) throw new Error(`GET /bluebutton/status failed: ${res.status}`);
  return await res.json();
}

export async function disconnectBlueButton(clientId) {
  const res = await fetch(`${BASE}/bluebutton/disconnect/${clientId}`, fetchOptions({
    method: "DELETE"
  }));
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || `DELETE /bluebutton/disconnect failed: ${res.status}`);
  }
  return await res.json();
}

export async function syncBlueButtonData(clientId) {
  const res = await fetch(`${BASE}/bluebutton/sync/${clientId}`, fetchOptions({
    method: "POST"
  }));
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || `POST /bluebutton/sync failed: ${res.status}`);
  }
  return await res.json();
}

export async function getBlueButtonClaims(clientId, limit = 50, offset = 0) {
  const res = await fetch(`${BASE}/bluebutton/claims/${clientId}?limit=${limit}&offset=${offset}`, fetchOptions());
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || `GET /bluebutton/claims failed: ${res.status}`);
  }
  return await res.json();
}

export async function getBlueButtonChanges(clientId, limit = 20) {
  const res = await fetch(`${BASE}/bluebutton/changes/${clientId}?limit=${limit}`, fetchOptions());
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || `GET /bluebutton/changes failed: ${res.status}`);
  }
  return await res.json();
}

export async function reviewPrescriptionChange(changeId) {
  const res = await fetch(`${BASE}/bluebutton/changes/${changeId}/review`, fetchOptions({
    method: "POST"
  }));
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || `POST /bluebutton/changes/review failed: ${res.status}`);
  }
  return await res.json();
}

export async function getMedicalEvents(clientId, limit = 50) {
  const res = await fetch(`${BASE}/bluebutton/medical-events/${clientId}?limit=${limit}`, fetchOptions());
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || `GET /bluebutton/medical-events failed: ${res.status}`);
  }
  return await res.json();
}

export async function getPlanHistory(clientId) {
  const res = await fetch(`${BASE}/bluebutton/plan-history/${clientId}`, fetchOptions());
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || `GET /bluebutton/plan-history failed: ${res.status}`);
  }
  return await res.json();
}

export async function getEligibilityStatus(clientId) {
  const res = await fetch(`${BASE}/bluebutton/eligibility/${clientId}`, fetchOptions());
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || `GET /bluebutton/eligibility failed: ${res.status}`);
  }
  return await res.json();
}

export async function getMedicalEventDetails(clientId, eventId) {
  const res = await fetch(`${BASE}/bluebutton/medical-events/${clientId}/${eventId}`, fetchOptions());
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || `GET /bluebutton/medical-events detail failed: ${res.status}`);
  }
  return await res.json();
}

export async function getPrescriptionClaimDetails(clientId, claimId) {
  const res = await fetch(`${BASE}/bluebutton/claims/${clientId}/${claimId}`, fetchOptions());
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || `GET /bluebutton/claims detail failed: ${res.status}`);
  }
  return await res.json();
}

export async function sendBlueButtonLinkEmail(clientId) {
  const res = await fetch(`${BASE}/bluebutton/send-link/email/${clientId}`, fetchOptions({
    method: "POST"
  }));
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || `POST /bluebutton/send-link/email failed: ${res.status}`);
  }
  return await res.json();
}

export async function sendBlueButtonLinkSMS(clientId) {
  const res = await fetch(`${BASE}/bluebutton/send-link/sms/${clientId}`, fetchOptions({
    method: "POST"
  }));
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || `POST /bluebutton/send-link/sms failed: ${res.status}`);
  }
  return await res.json();
}

// ============================================================================
// PATIENT ACCESS APIs (Generic Carrier OAuth)
// ============================================================================

// Get list of configured carriers (have credentials)
export async function getPatientAccessCarriers() {
  const res = await fetch(`${BASE}/patient-access/carriers`, fetchOptions());
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || `GET /patient-access/carriers failed: ${res.status}`);
  }
  return await res.json();
}

// Match client's carrier name to a Patient Access carrier
export async function matchPatientAccessCarrier(carrierName) {
  const res = await fetch(`${BASE}/patient-access/match/${encodeURIComponent(carrierName)}`, fetchOptions());
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || `GET /patient-access/match failed: ${res.status}`);
  }
  return await res.json();
}

// Send carrier OAuth link via email
export async function sendCarrierLinkEmail(carrierId, clientId) {
  const res = await fetch(`${BASE}/patient-access/${carrierId}/send-link/email/${clientId}`, fetchOptions({
    method: "POST"
  }));
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || `POST /patient-access send-link/email failed: ${res.status}`);
  }
  return await res.json();
}

// Send carrier OAuth link via SMS
export async function sendCarrierLinkSMS(carrierId, clientId) {
  const res = await fetch(`${BASE}/patient-access/${carrierId}/send-link/sms/${clientId}`, fetchOptions({
    method: "POST"
  }));
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || `POST /patient-access send-link/sms failed: ${res.status}`);
  }
  return await res.json();
}

// Get carrier connection status
export async function getCarrierConnectionStatus(carrierId, clientId) {
  const res = await fetch(`${BASE}/patient-access/${carrierId}/status/${clientId}`, fetchOptions());
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || `GET /patient-access status failed: ${res.status}`);
  }
  return await res.json();
}

// Get all carrier connections for a client
export async function getClientCarrierConnections(clientId) {
  const res = await fetch(`${BASE}/patient-access/connections/${clientId}`, fetchOptions());
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || `GET /patient-access connections failed: ${res.status}`);
  }
  return await res.json();
}

// Disconnect a carrier
export async function disconnectCarrier(carrierId, clientId) {
  const res = await fetch(`${BASE}/patient-access/${carrierId}/disconnect/${clientId}`, fetchOptions({
    method: "POST"
  }));
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || `POST /patient-access disconnect failed: ${res.status}`);
  }
  return await res.json();
}

// Manually trigger carrier data sync
export async function triggerCarrierSync(carrierId, clientId) {
  const res = await fetch(`${BASE}/patient-access/${carrierId}/sync/${clientId}`, fetchOptions({
    method: "POST"
  }));
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || `POST /patient-access sync failed: ${res.status}`);
  }
  return await res.json();
}

// Re-classify encounters based on encounter name (admin only)
export async function reclassifyEncounters(clientId) {
  const res = await fetch(`${BASE}/patient-access/reclassify-encounters/${clientId}`, fetchOptions({
    method: "POST"
  }));
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || `POST reclassify-encounters failed: ${res.status}`);
  }
  return await res.json();
}

// ============================================================================
// COMPREHENSIVE HEALTH DATA APIs
// ============================================================================

export async function getHealthDataSummary(clientId) {
  const res = await fetch(`${BASE}/bluebutton/summary/${clientId}`, fetchOptions());
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || `GET /bluebutton/summary failed: ${res.status}`);
  }
  return await res.json();
}

export async function getOutpatientClaims(clientId, limit = 50) {
  const res = await fetch(`${BASE}/bluebutton/outpatient/${clientId}?limit=${limit}`, fetchOptions());
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || `GET /bluebutton/outpatient failed: ${res.status}`);
  }
  return await res.json();
}

export async function getDMEClaims(clientId, limit = 20) {
  const res = await fetch(`${BASE}/bluebutton/dme/${clientId}?limit=${limit}`, fetchOptions());
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || `GET /bluebutton/dme failed: ${res.status}`);
  }
  return await res.json();
}

export async function getSNFStays(clientId) {
  const res = await fetch(`${BASE}/bluebutton/snf/${clientId}`, fetchOptions());
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || `GET /bluebutton/snf failed: ${res.status}`);
  }
  return await res.json();
}

export async function getHospiceEpisodes(clientId) {
  const res = await fetch(`${BASE}/bluebutton/hospice/${clientId}`, fetchOptions());
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || `GET /bluebutton/hospice failed: ${res.status}`);
  }
  return await res.json();
}

export async function getClientProviders(clientId) {
  const res = await fetch(`${BASE}/bluebutton/providers/${clientId}`, fetchOptions());
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || `GET /bluebutton/providers failed: ${res.status}`);
  }
  return await res.json();
}

export async function getCostAnalytics(clientId) {
  const res = await fetch(`${BASE}/bluebutton/costs/${clientId}`, fetchOptions());
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || `GET /bluebutton/costs failed: ${res.status}`);
  }
  return await res.json();
}

export async function getCoveragePeriods(clientId) {
  const res = await fetch(`${BASE}/bluebutton/coverage/${clientId}`, fetchOptions());
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || `GET /bluebutton/coverage failed: ${res.status}`);
  }
  return await res.json();
}

export async function uploadClientPhoto(clientId, file) {
  const formData = new FormData();
  formData.append('photo', file);

  const res = await fetch(`${BASE}/clients/${clientId}/photo`, {
    method: "POST",
    credentials: 'include',
    body: formData
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || `POST /clients/${clientId}/photo failed: ${res.status}`);
  }
  return await res.json();
}

export async function deleteClientPhoto(clientId) {
  const res = await fetch(`${BASE}/clients/${clientId}/photo`, fetchOptions({
    method: "DELETE"
  }));
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || `DELETE /clients/${clientId}/photo failed: ${res.status}`);
  }
  return await res.json();
}

// ============================================================================
// CHURN TRACKING
// ============================================================================

export async function getChurnReasons() {
  const res = await fetch(`${BASE}/churn/reasons`, fetchOptions());
  if (!res.ok) throw new Error(`GET /churn/reasons failed: ${res.status}`);
  return await res.json();
}

export async function logChurnEvent(data) {
  const res = await fetch(`${BASE}/churn/log`, {
    method: "POST",
    ...fetchOptions({ headers: { "Content-Type": "application/json" } }),
    body: JSON.stringify(data)
  });
  if (!res.ok) throw new Error(`POST /churn/log failed: ${res.status}`);
  return await res.json();
}

export async function getChurnEvents(params = {}) {
  const query = new URLSearchParams(params).toString();
  const res = await fetch(`${BASE}/churn/events${query ? '?' + query : ''}`, {
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`GET /churn/events failed: ${res.status}`);
  return await res.json();
}

export async function getChurnEventById(id) {
  const res = await fetch(`${BASE}/churn/events/${id}`, fetchOptions());
  if (!res.ok) throw new Error(`GET /churn/events/${id} failed: ${res.status}`);
  return await res.json();
}

export async function logWinbackAttempt(data) {
  const res = await fetch(`${BASE}/churn/winback`, {
    method: "POST",
    ...fetchOptions({ headers: { "Content-Type": "application/json" } }),
    body: JSON.stringify(data)
  });
  if (!res.ok) throw new Error(`POST /churn/winback failed: ${res.status}`);
  return await res.json();
}

export async function logRetentionSuccess(data) {
  const res = await fetch(`${BASE}/churn/retention-success`, {
    method: "POST",
    ...fetchOptions({ headers: { "Content-Type": "application/json" } }),
    body: JSON.stringify(data)
  });
  if (!res.ok) throw new Error(`POST /churn/retention-success failed: ${res.status}`);
  return await res.json();
}

export async function getChurnStats(params = {}) {
  const query = new URLSearchParams(params).toString();
  const res = await fetch(`${BASE}/churn/stats${query ? '?' + query : ''}`, {
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`GET /churn/stats failed: ${res.status}`);
  return await res.json();
}

// ============================================================================
// RISK SCORING
// ============================================================================

export async function getRiskFactors() {
  const res = await fetch(`${BASE}/risk/factors`, fetchOptions());
  if (!res.ok) throw new Error(`GET /risk/factors failed: ${res.status}`);
  return await res.json();
}

export async function getClientRisk(clientId) {
  const res = await fetch(`${BASE}/risk/client/${clientId}`, fetchOptions());
  if (!res.ok) throw new Error(`GET /risk/client failed: ${res.status}`);
  return await res.json();
}

export async function recalculateClientRisk(clientId) {
  const res = await fetch(`${BASE}/risk/client/${clientId}/recalculate`, {
    method: "POST", ...fetchOptions()
  });
  if (!res.ok) throw new Error(`POST /risk/recalculate failed: ${res.status}`);
  return await res.json();
}

export async function getRiskDashboard() {
  const res = await fetch(`${BASE}/risk/dashboard`, fetchOptions());
  if (!res.ok) throw new Error(`GET /risk/dashboard failed: ${res.status}`);
  return await res.json();
}

export async function getAtRiskClients(params = {}) {
  const query = new URLSearchParams(params).toString();
  const res = await fetch(`${BASE}/risk/at-risk-clients${query ? '?' + query : ''}`, {
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`GET /risk/at-risk-clients failed: ${res.status}`);
  return await res.json();
}

// Get intervention metrics for founder dashboard
export async function getInterventionMetrics(params = {}) {
  const query = new URLSearchParams(params).toString();
  const res = await fetch(`${BASE}/risk/intervention-metrics${query ? '?' + query : ''}`, {
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`GET /risk/intervention-metrics failed: ${res.status}`);
  return await res.json();
}

// Log an intervention from the Risk Management page
export async function logRiskIntervention(data) {
  const res = await fetch(`${BASE}/risk/intervention`, {
    method: "POST",
    ...fetchOptions({ headers: { "Content-Type": "application/json" } }),
    body: JSON.stringify(data)
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || `POST /risk/intervention failed: ${res.status}`);
  }
  return await res.json();
}

// Get detailed risk breakdown for a client
export async function getClientRiskBreakdown(clientId) {
  const res = await fetch(`${BASE}/risk/client/${clientId}/breakdown`, {
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`GET /risk/client/${clientId}/breakdown failed: ${res.status}`);
  return await res.json();
}

// ============================================================================
// CHURN PREDICTION MODEL
// ============================================================================

// Get current risk score for a client
export async function getChurnPredictionScore(clientId) {
  const res = await fetch(`${BASE}/churn-prediction/score/${clientId}`, {
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`GET /churn-prediction/score failed: ${res.status}`);
  const data = await res.json();
  // Extract the score object from the response and normalize the structure
  if (data.ok && data.score) {
    // Map allFactors to factors format expected by RiskScoreCard
    const factors = (data.score.allFactors || []).map(f => ({
      name: f.name,
      category: f.category,
      points: f.points,
      value: f.value || null
    }));

    // Map categoryScores to categoryBreakdown format
    const categoryBreakdown = {};
    if (data.score.categoryScores) {
      for (const [key, val] of Object.entries(data.score.categoryScores)) {
        // Convert camelCase keys to snake_case for consistency with UI
        const uiKey = key === 'benefitFit' ? 'benefit_fit' :
                      key === 'lifeEvents' ? 'life_events' : key;
        categoryBreakdown[uiKey] = val.weightedScore || 0;
      }
    }

    return {
      score: data.score.finalScore ?? data.score.score ?? 0,
      factors,
      categoryBreakdown,
      autoTrigger: data.score.auto100Reason || null,
      lastScored: data.score.lastScored || new Date().toISOString(),
      riskCategory: data.score.riskCategory?.name || null,
      isAuto100: data.score.isAuto100 || false
    };
  }
  return data;
}

// Trigger score recalculation for a client
export async function recalculateChurnScore(clientId) {
  const res = await fetch(`${BASE}/churn-prediction/recalculate/${clientId}`, {
    method: "POST",
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`POST /churn-prediction/recalculate failed: ${res.status}`);
  const data = await res.json();
  // Normalize response to match expected structure
  if (data.ok) {
    // After recalculate, fetch the full score details to get factors
    const fullScore = await getChurnPredictionScore(clientId);
    return {
      score: data.score ?? fullScore.score ?? 0,
      factors: fullScore.factors || [],
      categoryBreakdown: fullScore.categoryBreakdown || {},
      riskCategory: data.riskCategory || fullScore.riskCategory,
      autoTrigger: data.auto100Reason || fullScore.autoTrigger || null,
      isAuto100: data.isAuto100 || fullScore.isAuto100 || false
    };
  }
  return data;
}

// Get call outcome options for the modal
export async function getCallOutcomeOptions() {
  const res = await fetch(`${BASE}/churn-prediction/call-outcome-options`, {
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`GET /churn-prediction/call-outcome-options failed: ${res.status}`);
  return await res.json();
}

// Log a call outcome (from the modal)
export async function logCallOutcome(data) {
  const res = await fetch(`${BASE}/churn-prediction/call-outcome`, {
    method: "POST",
    ...fetchOptions({ headers: { "Content-Type": "application/json" } }),
    body: JSON.stringify(data)
  });
  if (!res.ok) throw new Error(`POST /churn-prediction/call-outcome failed: ${res.status}`);
  return await res.json();
}

// Get alerts for the current agent (or all if admin)
export async function getChurnAlerts(params = {}) {
  const query = new URLSearchParams(params).toString();
  const res = await fetch(`${BASE}/churn-prediction/alerts${query ? '?' + query : ''}`, {
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`GET /churn-prediction/alerts failed: ${res.status}`);
  return await res.json();
}

// Dismiss an alert
export async function dismissChurnAlert(alertId) {
  const res = await fetch(`${BASE}/churn-prediction/alerts/${alertId}/dismiss`, {
    method: "POST",
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`POST /churn-prediction/alerts/dismiss failed: ${res.status}`);
  return await res.json();
}

// Get morning briefing for current agent
export async function getMorningBriefing(date = null) {
  const query = date ? `?date=${date}` : '';
  const res = await fetch(`${BASE}/churn-prediction/morning-briefing${query}`, {
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`GET /churn-prediction/morning-briefing failed: ${res.status}`);
  return await res.json();
}

// Get priority call queue
export async function getPriorityQueue(params = {}) {
  const query = new URLSearchParams(params).toString();
  const res = await fetch(`${BASE}/churn-prediction/priority-queue${query ? '?' + query : ''}`, {
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`GET /churn-prediction/priority-queue failed: ${res.status}`);
  const data = await res.json();
  // Normalize response - backend returns { queue: [...] }, component expects { clients: [...] }
  if (data.ok && data.queue) {
    return { clients: data.queue, total: data.total };
  }
  return data;
}

// Log client contact
export async function logClientContact(data) {
  const res = await fetch(`${BASE}/churn-prediction/contact`, {
    method: "POST",
    ...fetchOptions({ headers: { "Content-Type": "application/json" } }),
    body: JSON.stringify(data)
  });
  if (!res.ok) throw new Error(`POST /churn-prediction/contact failed: ${res.status}`);
  return await res.json();
}

// Complete annual review for a client
// This logs the contact with is_annual_review=true, triggers Lambda scoring, and returns updated score
export async function completeAnnualReview(clientId, notes = '') {
  const res = await fetch(`${BASE}/churn-prediction/complete-annual-review`, {
    method: "POST",
    ...fetchOptions({ headers: { "Content-Type": "application/json" } }),
    body: JSON.stringify({ clientId, notes })
  });
  if (!res.ok) throw new Error(`POST /churn-prediction/complete-annual-review failed: ${res.status}`);
  return await res.json();
}

// Update SEP status for a client
export async function updateSepStatus(clientId, data) {
  const res = await fetch(`${BASE}/churn-prediction/sep-status/${clientId}`, {
    method: "POST",
    ...fetchOptions({ headers: { "Content-Type": "application/json" } }),
    body: JSON.stringify(data)
  });
  if (!res.ok) throw new Error(`POST /churn-prediction/sep-status failed: ${res.status}`);
  return await res.json();
}

// Get SEP status for a client
export async function getSepStatus(clientId) {
  const res = await fetch(`${BASE}/churn-prediction/sep-status/${clientId}`, {
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`GET /churn-prediction/sep-status failed: ${res.status}`);
  return await res.json();
}

// Get risk score history for a client
export async function getRiskHistory(clientId, params = {}) {
  const query = new URLSearchParams(params).toString();
  const res = await fetch(`${BASE}/churn-prediction/history/${clientId}${query ? '?' + query : ''}`, {
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`GET /churn-prediction/history failed: ${res.status}`);
  return await res.json();
}

// Log an intervention
export async function logIntervention(data) {
  const res = await fetch(`${BASE}/churn-prediction/intervention`, {
    method: "POST",
    ...fetchOptions({ headers: { "Content-Type": "application/json" } }),
    body: JSON.stringify(data)
  });
  if (!res.ok) throw new Error(`POST /churn-prediction/intervention failed: ${res.status}`);
  return await res.json();
}

// Get risk distribution (for charts)
export async function getRiskDistribution() {
  const res = await fetch(`${BASE}/churn-prediction/distribution`, {
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`GET /churn-prediction/distribution failed: ${res.status}`);
  const data = await res.json();
  // Normalize response - backend returns { distribution: {severe, critical, ...}, categories: [...] }
  // Component expects { total, categories: { severe: n, critical: n, ... } }
  if (data.ok && data.distribution) {
    return {
      total: parseInt(data.distribution.total) || 0,
      categories: {
        stable: parseInt(data.distribution.stable) || 0,
        low: parseInt(data.distribution.low) || 0,
        moderate: parseInt(data.distribution.moderate) || parseInt(data.distribution.medium) || 0,
        elevated: parseInt(data.distribution.elevated) || 0,
        high: parseInt(data.distribution.high) || 0,
        critical: parseInt(data.distribution.critical) || 0,
        severe: parseInt(data.distribution.severe) || 0
      }
    };
  }
  return data;
}

// ============================================================================
// FOUNDER ANALYTICS
// ============================================================================

// Get overview metrics
export async function getFounderOverview(params = {}) {
  const query = new URLSearchParams(params).toString();
  const res = await fetch(`${BASE}/founder-analytics/overview${query ? '?' + query : ''}`, {
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`GET /founder-analytics/overview failed: ${res.status}`);
  return await res.json();
}

// Get model accuracy metrics
export async function getModelAccuracy(params = {}) {
  const query = new URLSearchParams(params).toString();
  const res = await fetch(`${BASE}/founder-analytics/model-accuracy${query ? '?' + query : ''}`, {
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`GET /founder-analytics/model-accuracy failed: ${res.status}`);
  return await res.json();
}

// Get churn analysis breakdown
export async function getChurnAnalysis(params = {}) {
  const query = new URLSearchParams(params).toString();
  const res = await fetch(`${BASE}/founder-analytics/churn-analysis${query ? '?' + query : ''}`, {
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`GET /founder-analytics/churn-analysis failed: ${res.status}`);
  return await res.json();
}

// Get intervention effectiveness
export async function getInterventionEffectiveness(params = {}) {
  const query = new URLSearchParams(params).toString();
  const res = await fetch(`${BASE}/founder-analytics/intervention-effectiveness${query ? '?' + query : ''}`, {
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`GET /founder-analytics/intervention-effectiveness failed: ${res.status}`);
  return await res.json();
}

// Get risk factor analysis
export async function getRiskFactorAnalysis(params = {}) {
  const query = new URLSearchParams(params).toString();
  const res = await fetch(`${BASE}/founder-analytics/risk-factor-analysis${query ? '?' + query : ''}`, {
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`GET /founder-analytics/risk-factor-analysis failed: ${res.status}`);
  return await res.json();
}

// Get agent performance rankings
export async function getAgentPerformance(params = {}) {
  const query = new URLSearchParams(params).toString();
  const res = await fetch(`${BASE}/founder-analytics/agent-performance${query ? '?' + query : ''}`, {
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`GET /founder-analytics/agent-performance failed: ${res.status}`);
  return await res.json();
}

// Get competitor analysis
export async function getCompetitorAnalysis(params = {}) {
  const query = new URLSearchParams(params).toString();
  const res = await fetch(`${BASE}/founder-analytics/competitors${query ? '?' + query : ''}`, {
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`GET /founder-analytics/competitors failed: ${res.status}`);
  return await res.json();
}

// Get weight adjustment recommendations
export async function getWeightRecommendations() {
  const res = await fetch(`${BASE}/founder-analytics/weight-recommendations`, {
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`GET /founder-analytics/weight-recommendations failed: ${res.status}`);
  return await res.json();
}

// Get model version history
export async function getModelVersions() {
  const res = await fetch(`${BASE}/founder-analytics/model-versions`, {
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`GET /founder-analytics/model-versions failed: ${res.status}`);
  return await res.json();
}

// Trigger ML training manually
export async function triggerMLTraining() {
  const res = await fetch(`${BASE}/founder-analytics/trigger-training`, {
    method: "POST",
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`POST /founder-analytics/trigger-training failed: ${res.status}`);
  return await res.json();
}

// Generate analytics report
export async function generateAnalyticsReport(params = {}) {
  const res = await fetch(`${BASE}/founder-analytics/generate-report`, {
    method: "POST",
    ...fetchOptions({ headers: { "Content-Type": "application/json" } }),
    body: JSON.stringify(params)
  });
  if (!res.ok) throw new Error(`POST /founder-analytics/generate-report failed: ${res.status}`);
  return await res.json();
}

// ============================================================================
// AEP WIZARD
// ============================================================================

// Templates
export async function getAepTemplates() {
  const res = await fetch(`${BASE}/aep/templates`, fetchOptions());
  if (!res.ok) throw new Error(`GET /aep/templates failed: ${res.status}`);
  return await res.json();
}

export async function getAepTemplate(id) {
  const res = await fetch(`${BASE}/aep/templates/${id}`, fetchOptions());
  if (!res.ok) throw new Error(`GET /aep/templates/${id} failed: ${res.status}`);
  return await res.json();
}

export async function createAepTemplate(data) {
  const res = await fetch(`${BASE}/aep/templates`, {
    method: "POST",
    ...fetchOptions({ headers: { "Content-Type": "application/json" } }),
    body: JSON.stringify(data)
  });
  if (!res.ok) throw new Error(`POST /aep/templates failed: ${res.status}`);
  return await res.json();
}

export async function updateAepTemplate(id, data) {
  const res = await fetch(`${BASE}/aep/templates/${id}`, {
    method: "PUT",
    ...fetchOptions({ headers: { "Content-Type": "application/json" } }),
    body: JSON.stringify(data)
  });
  if (!res.ok) throw new Error(`PUT /aep/templates/${id} failed: ${res.status}`);
  return await res.json();
}

export async function deleteAepTemplate(id) {
  const res = await fetch(`${BASE}/aep/templates/${id}`, {
    method: "DELETE",
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`DELETE /aep/templates/${id} failed: ${res.status}`);
  return await res.json();
}

export async function getAepMergeTags() {
  const res = await fetch(`${BASE}/aep/merge-tags`, fetchOptions());
  if (!res.ok) throw new Error(`GET /aep/merge-tags failed: ${res.status}`);
  return await res.json();
}

// Automations
export async function getAepAutomations() {
  const res = await fetch(`${BASE}/aep/automations`, fetchOptions());
  if (!res.ok) throw new Error(`GET /aep/automations failed: ${res.status}`);
  return await res.json();
}

export async function updateAepAutomations(data) {
  const res = await fetch(`${BASE}/aep/automations`, {
    method: "PUT",
    ...fetchOptions({ headers: { "Content-Type": "application/json" } }),
    body: JSON.stringify(data)
  });
  if (!res.ok) throw new Error(`PUT /aep/automations failed: ${res.status}`);
  return await res.json();
}

// Countdown Contacts
export async function getAepCountdownContacts(params = {}) {
  const query = new URLSearchParams(params).toString();
  const res = await fetch(`${BASE}/aep/countdown${query ? '?' + query : ''}`, fetchOptions());
  if (!res.ok) throw new Error(`GET /aep/countdown failed: ${res.status}`);
  return await res.json();
}

export async function addAepCountdownContact(data) {
  const res = await fetch(`${BASE}/aep/countdown`, {
    method: "POST",
    ...fetchOptions({ headers: { "Content-Type": "application/json" } }),
    body: JSON.stringify(data)
  });
  if (!res.ok) throw new Error(`POST /aep/countdown failed: ${res.status}`);
  return await res.json();
}

export async function updateAepCountdownContact(id, data) {
  const res = await fetch(`${BASE}/aep/countdown/${id}`, {
    method: "PUT",
    ...fetchOptions({ headers: { "Content-Type": "application/json" } }),
    body: JSON.stringify(data)
  });
  if (!res.ok) throw new Error(`PUT /aep/countdown/${id} failed: ${res.status}`);
  return await res.json();
}

export async function deleteAepCountdownContact(id) {
  const res = await fetch(`${BASE}/aep/countdown/${id}`, {
    method: "DELETE",
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`DELETE /aep/countdown/${id} failed: ${res.status}`);
  return await res.json();
}

export async function sendAepDrip(contactId, data) {
  const res = await fetch(`${BASE}/aep/countdown/${contactId}/send`, {
    method: "POST",
    ...fetchOptions({ headers: { "Content-Type": "application/json" } }),
    body: JSON.stringify(data)
  });
  if (!res.ok) throw new Error(`POST /aep/countdown/${contactId}/send failed: ${res.status}`);
  return await res.json();
}

// Activity
export async function getAepActivity(params = {}) {
  const query = new URLSearchParams(params).toString();
  const res = await fetch(`${BASE}/aep/activity${query ? '?' + query : ''}`, fetchOptions());
  if (!res.ok) throw new Error(`GET /aep/activity failed: ${res.status}`);
  return await res.json();
}

export async function resendAepActivity(activityId) {
  const res = await fetch(`${BASE}/aep/activity/${activityId}/resend`, {
    method: "POST",
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`POST /aep/activity/${activityId}/resend failed: ${res.status}`);
  return await res.json();
}

// Analytics
export async function getAepAnalytics(params = {}) {
  const query = new URLSearchParams(params).toString();
  const res = await fetch(`${BASE}/aep/analytics${query ? '?' + query : ''}`, fetchOptions());
  if (!res.ok) throw new Error(`GET /aep/analytics failed: ${res.status}`);
  return await res.json();
}

// Blast Send
export async function sendAepBlast(data) {
  const res = await fetch(`${BASE}/aep/blast`, {
    method: "POST",
    ...fetchOptions({ headers: { "Content-Type": "application/json" } }),
    body: JSON.stringify(data)
  });
  if (!res.ok) throw new Error(`POST /aep/blast failed: ${res.status}`);
  return await res.json();
}

// Get clients for AEP outreach
export async function getAepClients(params = {}) {
  const query = new URLSearchParams(params).toString();
  const res = await fetch(`${BASE}/aep/clients${query ? '?' + query : ''}`, fetchOptions());
  if (!res.ok) throw new Error(`GET /aep/clients failed: ${res.status}`);
  return await res.json();
}
// ============================================================================
// OEP RETENTION HUB
// ============================================================================

// Templates
export async function getOepTemplates() {
  const res = await fetch(`${BASE}/oep/templates`, fetchOptions());
  if (!res.ok) throw new Error(`GET /oep/templates failed: ${res.status}`);
  return await res.json();
}

export async function createOepTemplate(data) {
  const res = await fetch(`${BASE}/oep/templates`, {
    method: "POST",
    ...fetchOptions({ headers: { "Content-Type": "application/json" } }),
    body: JSON.stringify(data)
  });
  if (!res.ok) throw new Error(`POST /oep/templates failed: ${res.status}`);
  return await res.json();
}

export async function updateOepTemplate(id, data) {
  const res = await fetch(`${BASE}/oep/templates/${id}`, {
    method: "PUT",
    ...fetchOptions({ headers: { "Content-Type": "application/json" } }),
    body: JSON.stringify(data)
  });
  if (!res.ok) throw new Error(`PUT /oep/templates/${id} failed: ${res.status}`);
  return await res.json();
}

export async function deleteOepTemplate(id) {
  const res = await fetch(`${BASE}/oep/templates/${id}`, {
    method: "DELETE",
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`DELETE /oep/templates/${id} failed: ${res.status}`);
  return await res.json();
}

export async function getOepMergeTags() {
  const res = await fetch(`${BASE}/oep/merge-tags`, fetchOptions());
  if (!res.ok) throw new Error(`GET /oep/merge-tags failed: ${res.status}`);
  return await res.json();
}

// Automations
export async function getOepAutomations() {
  const res = await fetch(`${BASE}/oep/automations`, fetchOptions());
  if (!res.ok) throw new Error(`GET /oep/automations failed: ${res.status}`);
  return await res.json();
}

export async function updateOepAutomations(data) {
  const res = await fetch(`${BASE}/oep/automations`, {
    method: "PUT",
    ...fetchOptions({ headers: { "Content-Type": "application/json" } }),
    body: JSON.stringify(data)
  });
  if (!res.ok) throw new Error(`PUT /oep/automations failed: ${res.status}`);
  return await res.json();
}

// Season info
export async function getOepSeason() {
  const res = await fetch(`${BASE}/oep/season`, fetchOptions());
  if (!res.ok) throw new Error(`GET /oep/season failed: ${res.status}`);
  return await res.json();
}

// Cohort
export async function getOepCohort(params = {}) {
  const query = new URLSearchParams(params).toString();
  const res = await fetch(`${BASE}/oep/cohort${query ? '?' + query : ''}`, fetchOptions());
  if (!res.ok) throw new Error(`GET /oep/cohort failed: ${res.status}`);
  return await res.json();
}

export async function autoPopulateOepCohort(seasonYear) {
  const res = await fetch(`${BASE}/oep/cohort/auto-populate`, {
    method: "POST",
    ...fetchOptions({ headers: { "Content-Type": "application/json" } }),
    body: JSON.stringify({ seasonYear })
  });
  if (!res.ok) throw new Error(`POST /oep/cohort/auto-populate failed: ${res.status}`);
  return await res.json();
}

export async function addToOepCohort(data) {
  const res = await fetch(`${BASE}/oep/cohort`, {
    method: "POST",
    ...fetchOptions({ headers: { "Content-Type": "application/json" } }),
    body: JSON.stringify(data)
  });
  if (!res.ok) throw new Error(`POST /oep/cohort failed: ${res.status}`);
  return await res.json();
}

export async function updateOepCohortMember(id, data) {
  const res = await fetch(`${BASE}/oep/cohort/${id}`, {
    method: "PUT",
    ...fetchOptions({ headers: { "Content-Type": "application/json" } }),
    body: JSON.stringify(data)
  });
  if (!res.ok) throw new Error(`PUT /oep/cohort/${id} failed: ${res.status}`);
  return await res.json();
}

export async function removeFromOepCohort(id) {
  const res = await fetch(`${BASE}/oep/cohort/${id}`, {
    method: "DELETE",
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`DELETE /oep/cohort/${id} failed: ${res.status}`);
  return await res.json();
}

export async function sendOepFollowUp(cohortId, data) {
  const res = await fetch(`${BASE}/oep/cohort/${cohortId}/send`, {
    method: "POST",
    ...fetchOptions({ headers: { "Content-Type": "application/json" } }),
    body: JSON.stringify(data)
  });
  if (!res.ok) throw new Error(`POST /oep/cohort/${cohortId}/send failed: ${res.status}`);
  return await res.json();
}

// Activity
export async function getOepActivity(params = {}) {
  const query = new URLSearchParams(params).toString();
  const res = await fetch(`${BASE}/oep/activity${query ? '?' + query : ''}`, fetchOptions());
  if (!res.ok) throw new Error(`GET /oep/activity failed: ${res.status}`);
  return await res.json();
}

export async function resendOepActivity(activityId) {
  const res = await fetch(`${BASE}/oep/activity/${activityId}/resend`, {
    method: "POST",
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`POST /oep/activity/${activityId}/resend failed: ${res.status}`);
  return await res.json();
}

// Analytics
export async function getOepAnalytics(params = {}) {
  const query = new URLSearchParams(params).toString();
  const res = await fetch(`${BASE}/oep/analytics${query ? '?' + query : ''}`, fetchOptions());
  if (!res.ok) throw new Error(`GET /oep/analytics failed: ${res.status}`);
  return await res.json();
}

// Blast Send
export async function sendOepBlast(data) {
  const res = await fetch(`${BASE}/oep/blast`, {
    method: "POST",
    ...fetchOptions({ headers: { "Content-Type": "application/json" } }),
    body: JSON.stringify(data)
  });
  if (!res.ok) throw new Error(`POST /oep/blast failed: ${res.status}`);
  return await res.json();
}

// Eligible clients for cohort
export async function getOepEligibleClients(params = {}) {
  const query = new URLSearchParams(params).toString();
  const res = await fetch(`${BASE}/oep/eligible-clients${query ? '?' + query : ''}`, fetchOptions());
  if (!res.ok) throw new Error(`GET /oep/eligible-clients failed: ${res.status}`);
  return await res.json();
}

// ============================================================================
// PILOT METRICS (Founder Command Center)
// ============================================================================

// Overview - Hero KPIs for the main dashboard
export async function getPilotOverview(params = {}) {
  const query = new URLSearchParams(params).toString();
  const res = await fetch(`${BASE}/pilot-metrics/overview${query ? '?' + query : ''}`, {
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`GET /pilot-metrics/overview failed: ${res.status}`);
  return await res.json();
}

// Agent Adoption Funnel
export async function getPilotAdoption() {
  const res = await fetch(`${BASE}/pilot-metrics/adoption`, {
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`GET /pilot-metrics/adoption failed: ${res.status}`);
  return await res.json();
}

// Adoption milestone detail (who completed/not completed a specific step)
export async function getPilotAdoptionDetail(milestone) {
  const res = await fetch(`${BASE}/pilot-metrics/adoption/${milestone}`, {
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`GET /pilot-metrics/adoption/${milestone} failed: ${res.status}`);
  return await res.json();
}

// Client Data Quality metrics
export async function getPilotClientQuality() {
  const res = await fetch(`${BASE}/pilot-metrics/client-quality`, {
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`GET /pilot-metrics/client-quality failed: ${res.status}`);
  return await res.json();
}

// Client quality by specific field
export async function getPilotClientQualityByField(field, params = {}) {
  const query = new URLSearchParams(params).toString();
  const res = await fetch(`${BASE}/pilot-metrics/client-quality/${field}${query ? '?' + query : ''}`, {
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`GET /pilot-metrics/client-quality/${field} failed: ${res.status}`);
  return await res.json();
}

// Alias for backwards compatibility
export const getPilotClientQualityDetail = getPilotClientQualityByField;

// Client quality broken down by agent
export async function getPilotClientQualityByAgent() {
  const res = await fetch(`${BASE}/pilot-metrics/client-quality/by-agent`, {
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`GET /pilot-metrics/client-quality/by-agent failed: ${res.status}`);
  return await res.json();
}

// Value Delivery metrics
export async function getPilotValue(params = {}) {
  const query = new URLSearchParams(params).toString();
  const res = await fetch(`${BASE}/pilot-metrics/value${query ? '?' + query : ''}`, {
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`GET /pilot-metrics/value failed: ${res.status}`);
  return await res.json();
}

// Risk alert details
export async function getPilotValueRiskAlerts(params = {}) {
  const query = new URLSearchParams(params).toString();
  const res = await fetch(`${BASE}/pilot-metrics/value/risk-alerts${query ? '?' + query : ''}`, {
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`GET /pilot-metrics/value/risk-alerts failed: ${res.status}`);
  return await res.json();
}

// Saved clients list
export async function getPilotValueSaved(params = {}) {
  const query = new URLSearchParams(params).toString();
  const res = await fetch(`${BASE}/pilot-metrics/value/saved${query ? '?' + query : ''}`, {
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`GET /pilot-metrics/value/saved failed: ${res.status}`);
  return await res.json();
}

// System Health metrics
export async function getPilotSystemHealth(params = {}) {
  const query = new URLSearchParams(params).toString();
  const res = await fetch(`${BASE}/pilot-metrics/system-health${query ? '?' + query : ''}`, {
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`GET /pilot-metrics/system-health failed: ${res.status}`);
  return await res.json();
}

// System error log
export async function getPilotSystemErrors(params = {}) {
  const query = new URLSearchParams(params).toString();
  const res = await fetch(`${BASE}/pilot-metrics/system-health/errors${query ? '?' + query : ''}`, {
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`GET /pilot-metrics/system-health/errors failed: ${res.status}`);
  return await res.json();
}

// Feature usage statistics
export async function getPilotFeatureUsage(params = {}) {
  const query = new URLSearchParams(params).toString();
  const res = await fetch(`${BASE}/pilot-metrics/feature-usage${query ? '?' + query : ''}`, {
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`GET /pilot-metrics/feature-usage failed: ${res.status}`);
  return await res.json();
}

// Daily trend snapshots
export async function getPilotTrends(params = {}) {
  const query = new URLSearchParams(params).toString();
  const res = await fetch(`${BASE}/pilot-metrics/trends${query ? '?' + query : ''}`, {
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`GET /pilot-metrics/trends failed: ${res.status}`);
  return await res.json();
}

// Generate daily snapshot (admin)
export async function generatePilotSnapshot() {
  const res = await fetch(`${BASE}/pilot-metrics/generate-snapshot`, {
    method: "POST",
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`POST /pilot-metrics/generate-snapshot failed: ${res.status}`);
  return await res.json();
}

// Mark client as saved (retention success)
export async function markClientSaved(clientId, data = {}) {
  const res = await fetch(`${BASE}/pilot-metrics/mark-saved/${clientId}`, {
    method: "POST",
    ...fetchOptions({ headers: { "Content-Type": "application/json" } }),
    body: JSON.stringify(data)
  });
  if (!res.ok) throw new Error(`POST /pilot-metrics/mark-saved failed: ${res.status}`);
  return await res.json();
}

// Mark client as churned
export async function markClientChurned(clientId, data = {}) {
  const res = await fetch(`${BASE}/pilot-metrics/mark-churned/${clientId}`, {
    method: "POST",
    ...fetchOptions({ headers: { "Content-Type": "application/json" } }),
    body: JSON.stringify(data)
  });
  if (!res.ok) throw new Error(`POST /pilot-metrics/mark-churned failed: ${res.status}`);
  return await res.json();
}

// ============================================================================
// COMPREHENSIVE PILOT METRICS - KPIs, Activity, Reports
// ============================================================================

// Get all 10 KPIs with trends
export async function getPilotKPIs() {
  const res = await fetch(`${BASE}/pilot-metrics/kpis`, {
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`GET /pilot-metrics/kpis failed: ${res.status}`);
  return await res.json();
}

// Get KPI history for charting
export async function getPilotKPIHistory(days = 30) {
  const res = await fetch(`${BASE}/pilot-metrics/kpis/history?days=${days}`, {
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`GET /pilot-metrics/kpis/history failed: ${res.status}`);
  return await res.json();
}

// Get activity metrics (DAU/WAU/MAU, sessions)
export async function getPilotActivity(period = '7_days') {
  const res = await fetch(`${BASE}/pilot-metrics/activity?period=${period}`, {
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`GET /pilot-metrics/activity failed: ${res.status}`);
  return await res.json();
}

// Get activity for specific agent
export async function getPilotAgentActivity(userId, days = 30) {
  const res = await fetch(`${BASE}/pilot-metrics/activity/agent/${userId}?days=${days}`, {
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`GET /pilot-metrics/activity/agent failed: ${res.status}`);
  return await res.json();
}

// Get engagement metrics (tasks, workflows)
export async function getPilotEngagement(period = '7_days') {
  const res = await fetch(`${BASE}/pilot-metrics/engagement?period=${period}`, {
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`GET /pilot-metrics/engagement failed: ${res.status}`);
  return await res.json();
}

// Get communication effectiveness metrics
export async function getPilotCommunicationEffectiveness(period = '7_days') {
  const res = await fetch(`${BASE}/pilot-metrics/communication-effectiveness?period=${period}`, {
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`GET /pilot-metrics/communication-effectiveness failed: ${res.status}`);
  return await res.json();
}

// Get risk scoring performance
export async function getPilotRiskPerformance() {
  const res = await fetch(`${BASE}/pilot-metrics/risk-performance`, {
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`GET /pilot-metrics/risk-performance failed: ${res.status}`);
  return await res.json();
}

// Get business impact metrics
export async function getPilotBusinessImpact(period = '30_days') {
  const res = await fetch(`${BASE}/pilot-metrics/business-impact?period=${period}`, {
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`GET /pilot-metrics/business-impact failed: ${res.status}`);
  return await res.json();
}

// Generate report (CSV, PDF, JSON)
export async function generatePilotReport(startDate, endDate, format = 'json', metrics = []) {
  const res = await fetch(`${BASE}/pilot-metrics/reports/generate`, {
    method: "POST",
    ...fetchOptions({ headers: { "Content-Type": "application/json" } }),
    body: JSON.stringify({ startDate, endDate, format, metrics })
  });
  if (!res.ok) throw new Error(`POST /pilot-metrics/reports/generate failed: ${res.status}`);
  if (format === 'csv') {
    return await res.text();
  }
  return await res.json();
}

// Verify churned clients (cross-reference)
export async function verifyChurnedClients() {
  const res = await fetch(`${BASE}/pilot-metrics/verify-churned`, {
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`GET /pilot-metrics/verify-churned failed: ${res.status}`);
  return await res.json();
}

// Fix churned client (mark as not churned)
export async function fixChurnedClient(clientId, newStatus = 'active', reason = '') {
  const res = await fetch(`${BASE}/pilot-metrics/fix-churned/${clientId}`, {
    method: "POST",
    ...fetchOptions({ headers: { "Content-Type": "application/json" } }),
    body: JSON.stringify({ newStatus, reason })
  });
  if (!res.ok) throw new Error(`POST /pilot-metrics/fix-churned failed: ${res.status}`);
  return await res.json();
}

// ============================================================================
// CRM INTEGRATIONS (Go High Level, Salesforce)
// ============================================================================

// Get all CRM integrations for current user
export async function getCRMIntegrations() {
  const res = await fetch(`${BASE}/crm/integrations`, {
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`GET /crm/integrations failed: ${res.status}`);
  return await res.json();
}

// Get a specific CRM integration with details
export async function getCRMIntegration(id) {
  const res = await fetch(`${BASE}/crm/integrations/${id}`, {
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`GET /crm/integrations/${id} failed: ${res.status}`);
  return await res.json();
}

// Initiate CRM OAuth connection
export async function connectCRM(crmType, locationId = null) {
  const params = new URLSearchParams();
  if (locationId) params.append('location_id', locationId);

  const res = await fetch(`${BASE}/crm/oauth/${crmType}/authorize?${params.toString()}`, {
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`GET /crm/oauth/${crmType}/authorize failed: ${res.status}`);
  return await res.json();
}

// Disconnect/delete a CRM integration
export async function disconnectCRM(integrationId) {
  const res = await fetch(`${BASE}/crm/integrations/${integrationId}`, {
    method: "DELETE",
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`DELETE /crm/integrations/${integrationId} failed: ${res.status}`);
  return await res.json();
}

// Update CRM integration settings
export async function updateCRMIntegration(integrationId, data) {
  const res = await fetch(`${BASE}/crm/integrations/${integrationId}`, {
    method: "PATCH",
    ...fetchOptions({ headers: { "Content-Type": "application/json" } }),
    body: JSON.stringify(data)
  });
  if (!res.ok) throw new Error(`PATCH /crm/integrations/${integrationId} failed: ${res.status}`);
  return await res.json();
}

// Trigger a CRM sync
export async function triggerCRMSync(integrationId, fullSync = false) {
  const res = await fetch(`${BASE}/crm/integrations/${integrationId}/sync`, {
    method: "POST",
    ...fetchOptions({ headers: { "Content-Type": "application/json" } }),
    body: JSON.stringify({ full_sync: fullSync })
  });
  if (!res.ok) {
    const errText = await res.text().catch(() => res.statusText);
    throw new Error(errText || `POST /crm/integrations/${integrationId}/sync failed: ${res.status}`);
  }
  return await res.json();
}

// Get sync history for an integration
export async function getCRMSyncHistory(integrationId, params = {}) {
  const query = new URLSearchParams(params).toString();
  const res = await fetch(`${BASE}/crm/integrations/${integrationId}/sync-history${query ? '?' + query : ''}`, {
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`GET /crm/integrations/${integrationId}/sync-history failed: ${res.status}`);
  return await res.json();
}

// Get mapped contacts for an integration
export async function getCRMMappedContacts(integrationId, params = {}) {
  const query = new URLSearchParams(params).toString();
  const res = await fetch(`${BASE}/crm/integrations/${integrationId}/contacts${query ? '?' + query : ''}`, {
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`GET /crm/integrations/${integrationId}/contacts failed: ${res.status}`);
  return await res.json();
}

// Get default field mapping for a CRM type
export async function getCRMFieldMapping(crmType) {
  const res = await fetch(`${BASE}/crm/field-mappings/${crmType}`, {
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`GET /crm/field-mappings/${crmType} failed: ${res.status}`);
  return await res.json();
}

// CMS Compliance APIs removed - keeping calls simple

// ============================================================================
// OTHER POLICIES (Life Insurance, Ancillary Medical, Annuities)
// ============================================================================

// Get all other policies for a client
export async function getOtherPolicies(clientId) {
  const res = await fetch(`${BASE}/api/clients/${clientId}/other-policies`, fetchOptions());
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || `GET /api/clients/${clientId}/other-policies failed: ${res.status}`);
  }
  return await res.json();
}

// LIFE INSURANCE
export async function createLifeInsurancePolicy(clientId, data) {
  const res = await fetch(`${BASE}/api/clients/${clientId}/life-insurance`, fetchOptions({
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data)
  }));
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || `POST /api/clients/${clientId}/life-insurance failed: ${res.status}`);
  }
  return await res.json();
}

export async function updateLifeInsurancePolicy(clientId, policyId, data) {
  const res = await fetch(`${BASE}/api/clients/${clientId}/life-insurance/${policyId}`, fetchOptions({
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data)
  }));
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || `PUT /api/clients/${clientId}/life-insurance/${policyId} failed: ${res.status}`);
  }
  return await res.json();
}

export async function deleteLifeInsurancePolicy(clientId, policyId) {
  const res = await fetch(`${BASE}/api/clients/${clientId}/life-insurance/${policyId}`, fetchOptions({
    method: "DELETE"
  }));
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || `DELETE /api/clients/${clientId}/life-insurance/${policyId} failed: ${res.status}`);
  }
  return await res.json();
}

// ANCILLARY MEDICAL
export async function createAncillaryPolicy(clientId, data) {
  const res = await fetch(`${BASE}/api/clients/${clientId}/ancillary`, fetchOptions({
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data)
  }));
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || `POST /api/clients/${clientId}/ancillary failed: ${res.status}`);
  }
  return await res.json();
}

export async function updateAncillaryPolicy(clientId, policyId, data) {
  const res = await fetch(`${BASE}/api/clients/${clientId}/ancillary/${policyId}`, fetchOptions({
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data)
  }));
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || `PUT /api/clients/${clientId}/ancillary/${policyId} failed: ${res.status}`);
  }
  return await res.json();
}

export async function deleteAncillaryPolicy(clientId, policyId) {
  const res = await fetch(`${BASE}/api/clients/${clientId}/ancillary/${policyId}`, fetchOptions({
    method: "DELETE"
  }));
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || `DELETE /api/clients/${clientId}/ancillary/${policyId} failed: ${res.status}`);
  }
  return await res.json();
}

// ANNUITIES
export async function createAnnuityPolicy(clientId, data) {
  const res = await fetch(`${BASE}/api/clients/${clientId}/annuity`, fetchOptions({
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data)
  }));
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || `POST /api/clients/${clientId}/annuity failed: ${res.status}`);
  }
  return await res.json();
}

export async function updateAnnuityPolicy(clientId, policyId, data) {
  const res = await fetch(`${BASE}/api/clients/${clientId}/annuity/${policyId}`, fetchOptions({
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data)
  }));
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || `PUT /api/clients/${clientId}/annuity/${policyId} failed: ${res.status}`);
  }
  return await res.json();
}

export async function deleteAnnuityPolicy(clientId, policyId) {
  const res = await fetch(`${BASE}/api/clients/${clientId}/annuity/${policyId}`, fetchOptions({
    method: "DELETE"
  }));
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || `DELETE /api/clients/${clientId}/annuity/${policyId} failed: ${res.status}`);
  }
  return await res.json();
}

// Default export with axios-style methods for convenience
const api = {
  get: async (url) => {
    const BASE = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
    const res = await fetch(`${BASE}${url}`, { credentials: 'include' });
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.error || `GET ${url} failed: ${res.status}`);
    }
    return { data: await res.json() };
  },
  post: async (url, data) => {
    const BASE = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
    const res = await fetch(`${BASE}${url}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: data ? JSON.stringify(data) : undefined
    });
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.error || `POST ${url} failed: ${res.status}`);
    }
    return { data: await res.json() };
  },
  put: async (url, data) => {
    const BASE = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
    const res = await fetch(`${BASE}${url}`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.error || `PUT ${url} failed: ${res.status}`);
    }
    return { data: await res.json() };
  },
  delete: async (url) => {
    const BASE = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
    const res = await fetch(`${BASE}${url}`, { method: 'DELETE', credentials: 'include' });
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.error || `DELETE ${url} failed: ${res.status}`);
    }
    return { data: await res.json() };
  }
};

// ============================================================================
// AUTOMATION COMMAND CENTER (Founder Only)
// ============================================================================

// Get kill switch states
export async function getKillSwitchStates() {
  const res = await fetch(`${BASE}/api/admin/kill-switch`, fetchOptions());
  if (!res.ok) throw new Error(`GET /api/admin/kill-switch failed: ${res.status}`);
  return await res.json();
}

// Toggle global kill switch
export async function toggleGlobalKillSwitch(paused, reason) {
  const res = await fetch(`${BASE}/api/admin/kill-switch/global`, {
    method: 'PUT',
    ...fetchOptions({ headers: { 'Content-Type': 'application/json' } }),
    body: JSON.stringify({ paused, reason })
  });
  if (!res.ok) throw new Error(`PUT /api/admin/kill-switch/global failed: ${res.status}`);
  return await res.json();
}

// Toggle channel kill switch
export async function toggleChannelKillSwitch(channel, paused, reason) {
  const res = await fetch(`${BASE}/api/admin/kill-switch/channel/${channel}`, {
    method: 'PUT',
    ...fetchOptions({ headers: { 'Content-Type': 'application/json' } }),
    body: JSON.stringify({ paused, reason })
  });
  if (!res.ok) throw new Error(`PUT /api/admin/kill-switch/channel failed: ${res.status}`);
  return await res.json();
}

// Toggle trigger kill switch
export async function toggleTriggerKillSwitch(triggerKey, paused, reason) {
  const res = await fetch(`${BASE}/api/admin/kill-switch/trigger/${triggerKey}`, {
    method: 'PUT',
    ...fetchOptions({ headers: { 'Content-Type': 'application/json' } }),
    body: JSON.stringify({ paused, reason })
  });
  if (!res.ok) throw new Error(`PUT /api/admin/kill-switch/trigger failed: ${res.status}`);
  return await res.json();
}

// Get automation stats for today
export async function getAutomationStatsToday() {
  const res = await fetch(`${BASE}/api/admin/automations/stats/today`, fetchOptions());
  if (!res.ok) throw new Error(`GET /api/admin/automations/stats/today failed: ${res.status}`);
  return await res.json();
}

// Get automation stats for specific date
export async function getAutomationStatsDate(date) {
  const res = await fetch(`${BASE}/api/admin/automations/stats/${date}`, fetchOptions());
  if (!res.ok) throw new Error(`GET /api/admin/automations/stats/${date} failed: ${res.status}`);
  return await res.json();
}

// Get OEP blast stats
export async function getOEPBlastStats(year) {
  const res = await fetch(`${BASE}/api/admin/automations/stats/oep/${year}`, fetchOptions());
  if (!res.ok) throw new Error(`GET /api/admin/automations/stats/oep failed: ${res.status}`);
  return await res.json();
}

// Get queue status
export async function getAutomationQueue(options = {}) {
  const params = new URLSearchParams();
  if (options.limit) params.append('limit', options.limit);
  if (options.status) params.append('status', options.status);
  if (options.automation_key) params.append('automation_key', options.automation_key);

  const res = await fetch(`${BASE}/api/admin/automations/queue?${params}`, fetchOptions());
  if (!res.ok) throw new Error(`GET /api/admin/automations/queue failed: ${res.status}`);
  return await res.json();
}

// Get activity log
export async function getAutomationActivity(hours = 24) {
  const res = await fetch(`${BASE}/api/admin/automations/activity?hours=${hours}`, fetchOptions());
  if (!res.ok) throw new Error(`GET /api/admin/automations/activity failed: ${res.status}`);
  return await res.json();
}

// Get all triggers
export async function getAutomationTriggers() {
  const res = await fetch(`${BASE}/api/admin/automations/triggers`, fetchOptions());
  if (!res.ok) throw new Error(`GET /api/admin/automations/triggers failed: ${res.status}`);
  return await res.json();
}

// Send test message
export async function sendAutomationTest(automation_key, channel, client_id, use_myself) {
  const res = await fetch(`${BASE}/api/admin/automations/test`, {
    method: 'POST',
    ...fetchOptions({ headers: { 'Content-Type': 'application/json' } }),
    body: JSON.stringify({ automation_key, channel, client_id, use_myself })
  });
  if (!res.ok) throw new Error(`POST /api/admin/automations/test failed: ${res.status}`);
  return await res.json();
}

// Force process queue
export async function forceProcessQueue() {
  const res = await fetch(`${BASE}/api/admin/automations/queue/process-now`, {
    method: 'POST',
    ...fetchOptions()
  });
  if (!res.ok) throw new Error(`POST /api/admin/automations/queue/process-now failed: ${res.status}`);
  return await res.json();
}

// Clear (cancel) all queued messages for an automation
export async function clearAutomationQueue(automationKey, userId = null) {
  const body = { automation_key: automationKey };
  if (userId) body.user_id = userId;

  const res = await fetch(`${BASE}/api/admin/automations/queue/clear`, {
    method: 'POST',
    ...fetchOptions({ headers: { 'Content-Type': 'application/json' } }),
    body: JSON.stringify(body)
  });
  if (!res.ok) throw new Error(`POST /api/admin/automations/queue/clear failed: ${res.status}`);
  return await res.json();
}

// Re-queue OEP automation (clear + re-populate with fresh templates)
export async function requeueAutomation(automationKey) {
  const res = await fetch(`${BASE}/api/admin/automations/queue/requeue`, {
    method: 'POST',
    ...fetchOptions({ headers: { 'Content-Type': 'application/json' } }),
    body: JSON.stringify({ automation_key: automationKey })
  });
  if (!res.ok) throw new Error(`POST /api/admin/automations/queue/requeue failed: ${res.status}`);
  return await res.json();
}

// Get kill switch audit log
export async function getKillSwitchAudit() {
  const res = await fetch(`${BASE}/api/admin/kill-switch/audit`, fetchOptions());
  if (!res.ok) throw new Error(`GET /api/admin/kill-switch/audit failed: ${res.status}`);
  return await res.json();
}

// ============================================================================
// MORNING BRIEF AUTOMATION PREVIEW (Agent-facing)
// ============================================================================

// Get today's scheduled automations (for Morning Brief)
export async function getTodayAutomationPreview() {
  const res = await fetch(`${BASE}/api/automations/today-preview`, fetchOptions());
  if (!res.ok) throw new Error(`GET /api/automations/today-preview failed: ${res.status}`);
  return await res.json();
}

// Get overnight job execution results (founder only)
export async function getOvernightJobs() {
  const res = await fetch(`${BASE}/morning-brief/overnight-jobs`, { credentials: 'include' });
  if (!res.ok) throw new Error(`GET /morning-brief/overnight-jobs failed: ${res.status}`);
  return await res.json();
}

// Trigger all daily automation jobs (founder only)
export async function runAllOvernightJobs() {
  const res = await fetch(`${BASE}/morning-brief/run-all-jobs`, {
    method: 'POST',
    credentials: 'include'
  });
  if (!res.ok) throw new Error(`POST /morning-brief/run-all-jobs failed: ${res.status}`);
  return await res.json();
}

export async function getScoringDetails() {
  const res = await fetch(`${BASE}/morning-brief/scoring-details`, { credentials: 'include' });
  if (!res.ok) throw new Error(`GET /morning-brief/scoring-details failed: ${res.status}`);
  return await res.json();
}

export async function deleteOvernightJob(jobId) {
  const res = await fetch(`${BASE}/morning-brief/overnight-job/${jobId}`, {
    method: 'DELETE',
    credentials: 'include'
  });
  if (!res.ok) throw new Error(`DELETE /morning-brief/overnight-job failed: ${res.status}`);
  return await res.json();
}

// Cancel a queued automation message
export async function cancelQueuedAutomation(messageId, reason = '') {
  const res = await fetch(`${BASE}/api/automations/queue/${messageId}/cancel`, {
    method: 'POST',
    ...fetchOptions({ headers: { 'Content-Type': 'application/json' } }),
    body: JSON.stringify({ reason })
  });
  if (!res.ok) throw new Error(`POST /api/automations/queue/${messageId}/cancel failed: ${res.status}`);
  return await res.json();
}

export default api;
